import {useMutation, useQuery, useLazyQuery} from '@apollo/client';
import {
  UpdateBulkAdmin,
  UpdateBulkAdminVariables,
} from '../../schema-types/UpdateBulkAdmin';
import {
  GetBulkAccounts,
  GetBulkAccountsVariables,
} from '../../schema-types/GetBulkAccounts';
import {
  BulkAdminSortableFieldsEnum,
  SortingDirectionEnum,
} from '../../schema-types/globalTypes';
import {
  BULK_UPDATE_ACCOUNTS,
  GET_ALL_ADMINS,
  GET_ALL_CASE_CHECKERS,
  GET_ALL_PARAPLANNERS,
  GET_ALL_SERVICE_MANAGERS,
  GET_ALL_SUPERVISORS,
  GET_BULK_ACCOUNTS,
  PULL_ACCOUNT,
} from './bulkAdminQueries';
import {PullAccount, PullAccountVariables} from '../../schema-types/PullAccount';
import {GetAllAdmins, GetAllAdmins_staff} from '../../schema-types/GetAllAdmins';
import {
  GetAllServiceManagers,
  GetAllServiceManagers_staff,
} from '../../schema-types/GetAllServiceManagers';
import {
  GetAllSupervisors,
  GetAllSupervisors_staff,
} from '../../schema-types/GetAllSupervisors';
import {
  GetAllParaplanners,
  GetAllParaplanners_staff,
} from '../../schema-types/GetAllParaplanners';
import {
  GetAllCaseCheckers,
  GetAllCaseCheckers_staff,
} from '../../schema-types/GetAllCaseCheckers';
import {useAllPadAdvisers} from '../../hooks/useAllPadAdvisers';
import {GetAllPadAdvisers_allPadAdvisers} from '../../schema-types/GetAllPadAdvisers';

type staff =
  | GetAllAdmins_staff
  | GetAllServiceManagers_staff
  | GetAllSupervisors_staff
  | GetAllParaplanners_staff
  | GetAllCaseCheckers_staff;

type SortAlphabeticallyByNameType = string | null;
export const sortAlphabeticallyByName = (
  a: {name: SortAlphabeticallyByNameType},
  b: {name: SortAlphabeticallyByNameType}
) => {
  const aName = a.name?.toLowerCase() || '';
  const bName = b.name?.toLowerCase() || '';
  if (aName < bName) {
    return -1;
  }
  if (aName > bName) {
    return 1;
  }
  return 0;
};

const filterPadStaff = (x: staff) => !!x.email && x.isPad;

export const initialBulkAdminSortable = BulkAdminSortableFieldsEnum.NEXT_REVIEW_DATE;
export const initialSortingDirection = SortingDirectionEnum.ASCENDING;
export const bulkAdminPageSize = 50;

/**
 * The reason why we don't expose hasPreviousPage is because it never works as intended
 * We ended up having to roll out our own solution to check if there's a previous page available
 */
export function useBulkAdmin() {
  const pageSize = bulkAdminPageSize;
  const [updateBulk, {data: updateData}] = useMutation<
    UpdateBulkAdmin,
    UpdateBulkAdminVariables
  >(BULK_UPDATE_ACCOUNTS);

  const [fetch, {data, loading, client}] = useLazyQuery<
    GetBulkAccounts,
    GetBulkAccountsVariables
  >(GET_BULK_ACCOUNTS);

  const {
    allPadAdvisers: advisersData,
    loading: advisersLoading,
    error: advisersError,
  } = useAllPadAdvisers();

  const {data: adminData, loading: aLoad} = useQuery<GetAllAdmins>(GET_ALL_ADMINS);
  const {data: smData, loading: smLoad} = useQuery<GetAllServiceManagers>(
    GET_ALL_SERVICE_MANAGERS
  );
  const {data: supData, loading: suLoad} =
    useQuery<GetAllSupervisors>(GET_ALL_SUPERVISORS);
  const {data: ppData, loading: ppLoad} =
    useQuery<GetAllParaplanners>(GET_ALL_PARAPLANNERS);
  const {data: ccData, loading: ccLoad} =
    useQuery<GetAllCaseCheckers>(GET_ALL_CASE_CHECKERS);

  const [pullAccount, {data: rowData, loading: rowLoading}] = useMutation<
    PullAccount,
    PullAccountVariables
  >(PULL_ACCOUNT);

  const padAdvisers =
    advisersData?.filter((x) => !!x.email).sort(sortAlphabeticallyByName) || [];
  const admins =
    adminData?.staff?.filter(filterPadStaff).sort(sortAlphabeticallyByName) || [];
  const sm = smData?.staff?.filter(filterPadStaff).sort(sortAlphabeticallyByName) || [];
  const sup = supData?.staff?.filter(filterPadStaff).sort(sortAlphabeticallyByName) || [];
  const pp = ppData?.staff?.filter(filterPadStaff).sort(sortAlphabeticallyByName) || [];
  const cc = ccData?.staff?.filter(filterPadStaff).sort(sortAlphabeticallyByName) || [];

  const owners: (staff | GetAllPadAdvisers_allPadAdvisers)[] = [
    ...admins,
    ...padAdvisers,
    ...sm,
    ...sup,
    ...pp,
    ...cc,
  ];

  return {
    updateBulk,
    bulkAccounts: data?.bulkAccounts?.edges?.map((x) => x?.node) || [],
    totalCount: data?.bulkAccounts?.totalCount || 0,
    loading,
    updateData,
    pageSize,
    fetch,
    client,
    startCursor: data?.bulkAccounts?.pageInfo.startCursor,
    endCursor: data?.bulkAccounts?.pageInfo.endCursor,
    hasNextPage: data?.bulkAccounts?.pageInfo.hasNextPage,
    advisersError,
    advisersLoading,
    advisers: padAdvisers,
    owners,
    ownersLoading: aLoad || smLoad || suLoad || ppLoad || ccLoad,
    pullAccount,
    rowData: rowData?.pullAccount?.account,
    rowLoading,
    allAdvisers: [...(advisersData ?? [])].sort(sortAlphabeticallyByName),
  };
}
